/**
 * <CoachAvatar coach={coachObject} size={40} ring />
 *
 * Renders a circular coach portrait (Nano-Banana-generated PNG) with graceful
 * onError fallback to the initials avatar so a missing file (e.g. ephemeral
 * disk wipe in production) never produces a broken-image icon.
 *
 * `size` is the diameter in px. `ring` adds a thin gold border. `className` is
 * appended for layout overrides. Always shoulder-up portrait, never stretched.
 */
import { useState } from "react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function CoachAvatar({ coach, size = 40, ring = false, className = "", ...rest }) {
  const [failed, setFailed] = useState(false);

  if (!coach) return null;

  const portraitSrc = coach.portrait
    ? (coach.portrait.startsWith("http") ? coach.portrait : `${API}${coach.portrait}`)
    : null;

  const style = {
    width: size,
    height: size,
    borderRadius: "50%",
    flexShrink: 0,
    objectFit: "cover",
    objectPosition: "center 25%",
    ...(ring ? { boxShadow: "0 0 0 1.5px rgba(201,168,76,0.5)" } : {}),
  };

  // Initials fallback — uses the coach's existing bg/fg palette colours
  if (!portraitSrc || failed) {
    return (
      <div
        className={`flex items-center justify-center font-bold ${className}`}
        style={{
          ...style,
          backgroundColor: coach.bg || "#0F1E35",
          color: coach.fg || "#C9A84C",
          fontSize: Math.max(10, Math.round(size * 0.36)),
          border: `2px solid ${coach.fg || "#C9A84C"}`,
        }}
        data-testid={`coach-avatar-initials-${coach.id}`}
        title={coach.name}
      >
        {coach.initials}
      </div>
    );
  }

  return (
    <img
      src={portraitSrc}
      alt={coach.name}
      onError={() => setFailed(true)}
      style={style}
      className={className}
      data-testid={`coach-avatar-${coach.id}`}
      {...rest}
    />
  );
}
